import { headerItems, mobileHeaderItems } from '@/utils/constants';
import { Icons } from '@/utils/Icons';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ProfileDrawer from './ProfileDrawer';
import LanguageSwitcher from './common/LanguageSwitcher';
import { useDispatch } from 'react-redux';
import { openModal } from '@/store';
import { useIsAuthenticated } from '@/hooks';
import {
  useGetUserProfileQuery,
  useGetUserWalletsQuery,
  useGetIsMasterOwnerQuery,
} from '@/store/api/userApi';
import { useGetSystemSettingsQuery } from '@/store/api/systemApi';
import { useGetFixtureCountsQuery } from '@/store/api/eventsApi';
import { useAppSelector, useIsDemoUser, useIsMasterOwnerUser } from '@/hooks/redux';
import { formatCompactNumber } from '@/utils';

// Maps a headerItems/mobileHeaderItems title to its translation key in header.sports —
// only needed where the title contains a "." (i18next's key separator)
const sportTitleI18nKey: Record<string, string> = {
  'int.casino': 'intCasino',
};

interface HeaderProps {
  sidebarOpen: boolean;
  setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

// Maps a headerItems/mobileHeaderItems title to the sport key used by /fixture/counts
const sportCountKey: Record<string, string> = {
  cricket: 'Cricket',
  soccer: 'Soccer',
  tennis: 'Tennis',
};

const Header = ({ sidebarOpen, setSidebarOpen }: HeaderProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [openDrawer, setOpenDrawer] = useState(false);
  const isAuthenticated = useIsAuthenticated();
  const isDemoUser = useIsDemoUser();
  const coinAmount = useAppSelector((state) => state.wallet.coinAmount);
  const isMasterOwnerUser = useIsMasterOwnerUser();
  const { data: systemSettings } = useGetSystemSettingsQuery(undefined, {
    pollingInterval: 30000,
    refetchOnMountOrArgChange: true,
  });

  useGetUserProfileQuery(undefined, { skip: !isAuthenticated });
  useGetIsMasterOwnerQuery(undefined, { skip: !isAuthenticated });

  const { data: wallets } = useGetUserWalletsQuery(undefined, {
    skip: !isAuthenticated,
    pollingInterval: 5000,
  });

  const mainWallet = wallets?.find((w) => w.type?.toLowerCase() === 'main');
  const mainBalance = (
    Number(mainWallet?.amount ?? 0) - Math.abs(Number(mainWallet?.exposureAmount ?? 0))
  ).toFixed(2);

  const { data: fixtureCounts } = useGetFixtureCountsQuery(undefined, {
    pollingInterval: 2000,
  });
  const inplaySportCounts = fixtureCounts?.counts?.inplay?.sports ?? {};

  return (
    <>
      <header className="bg-headerBg h-[70px] fixed text-primaryText w-full z-30 top-0 py-[5px]">
        {isDemoUser && (
          <div className="text-[13px] text-center flex justify-center items-center w-[145px] rounded-b-[5px] absolute top-[9px] left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#FF0000] text-white  md:py-1 z-10">
            {t('header.demoAccount')}
          </div>
        )}
        <div className="w-full px-1 sm:px-3 mx-auto h-full">
          <div className="flex items-center justify-between h-full">
            <div className="flex items-center cursor-pointer w-[300px]">
              <span className="mr-[7px]">
                <Icons.Menu
                  size={14}
                  className="text-white lg:hidden"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                />
              </span>
              <Link to="/">
                <img src="/images/logo.svg" className="h-[57px]" alt="" />
              </Link>
            </div>
            <div className="max-lg:hidden overflow-y-hidden w-full whitespace-nowrap">
              <div className="flex items-center gap-1 mx-auto px-4 rounded-[50px] relative z-50">
                {headerItems.map((item) => {
                  const countKey = sportCountKey[item.title];
                  const liveCount = countKey ? (inplaySportCounts[countKey] ?? 0) : undefined;

                  return (
                    <div
                      key={item.id}
                      className="text-headerText border-b-[3px] border-transparent group"
                    >
                      <Link
                        to={item.path}
                        className="relative flex flex-col items-center text-[11px] font-bold uppercase px-[10px] py-2"
                      >
                        {liveCount !== undefined && (
                          <div className="absolute top-2 -right-1 flex items-center whitespace-nowrap text-[8px] font-bold leading-none">
                            <div className=" bg-[#FBFBFB] text-[#EF1717] rounded-l-[3px] text-[8px] px-1 pb-0.5 pt-1">
                              <span className="animate-pulse">LIVE</span>
                            </div>
                            <div className="bg-[#EF1717] text-white rounded-r-[3px] text-[9px] px-1 h-[14px] flex items-center justify-center ">
                              {liveCount}
                            </div>
                          </div>
                        )}
                        <span
                          className={`flex size-8 overflow-hidden items-center justify-center rounded-[50px] group-hover:bg-white ${pathname === `${item.path}` ? 'bg-white' : 'bg-[#1f2c38]'}`}
                        >
                          <img className="w-8 h-auto" src={item.imgUrl} alt={item.title} />
                        </span>

                        {t(`header.sports.${sportTitleI18nKey[item.title] ?? item.title}`, {
                          defaultValue: item.title,
                        })}
                      </Link>
                    </div>
                  );
                })}
              </div>
            </div>
            <LanguageSwitcher className="hidden sm:flex text-white mr-2" />
            <div className="flex gap-[6px]">
              {isAuthenticated ? (
                <>
                  <div className="hidden md:flex lg:hidden gap-[6px]">
                    {!isDemoUser &&
                      (isMasterOwnerUser ? systemSettings?.deposit?.selection : true) && (
                        <Link
                          to="/deposit"
                          className="px-[16px] py-2 text-white text-[12px] sm:text-[14px] rounded-[50px] bg-[#111020] border border-secondaryBg flex items-center gap-1 justify-center font-bold [filter:var(--btn-dropshadow)] hover:bg-[#fec733]"
                        >
                          {t('header.deposit')}
                        </Link>
                      )}
                    {!isDemoUser &&
                      (isMasterOwnerUser ? systemSettings?.withdrawal?.selection : true) && (
                        <Link
                          to="/withdraw"
                          className="px-[16px] py-2 text-white text-[12px] sm:text-[14px] rounded-[50px] bg-[#111020] border border-secondaryBg flex items-center gap-1 justify-center font-bold [filter:var(--btn-dropshadow)] hover:bg-[#fec733]"
                        >
                          {t('header.withdraw')}
                        </Link>
                      )}
                  </div>
                  {!isDemoUser && (
                    <button
                      onClick={() => navigate('/coins')}
                      title={t('header.myCoins', { amount: coinAmount.toFixed(2) })}
                      className="relative flex shrink-0 items-center gap-1 sm:gap-1.5 overflow-hidden rounded-full border border-[#fec733]/70 bg-black px-2.5 sm:px-3.5 py-2 sm:py-3 font-bold text-[#fec733] [filter:var(--btn-dropshadow)]"
                    >
                      <Icons.Coin size={15} className="shrink-0" />
                      <span className="whitespace-nowrap text-[12px] leading-none sm:text-[14px]">
                        {formatCompactNumber(coinAmount)}
                      </span>
                    </button>
                  )}
                  <button
                    onClick={() => setOpenDrawer(true)}
                    className="w-[90%] px-[12px] py-3 text-white text-[14px] rounded-[50px] bg-[#111020] border border-secondaryBg flex items-center justify-center gap-1 font-bold [filter:var(--btn-dropshadow)]"
                  >
                    <span>💸</span>
                    {mainBalance}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => dispatch(openModal({ name: 'login' }))}
                    className="w-[90%] px-[25px] py-2 text-white text-nowrap text-[12px] sm:text-[14px] rounded-[50px] bg-[#111020] border border-secondaryBg flex items-center justify-center font-bold [filter:var(--btn-dropshadow)] hover:bg-[#fec733]"
                  >
                    {t('header.login')}
                  </button>
                  {systemSettings?.registration?.selection && (
                    <button
                      onClick={() => dispatch(openModal({ name: 'register' }))}
                      className="w-[90%] px-[25px] py-2 text-white text-nowrap text-[12px] sm:text-[14px] rounded-[50px] bg-[#111020] border border-secondaryBg flex items-center justify-center font-bold [filter:var(--btn-dropshadow)] hover:bg-[#fec733]"
                    >
                      {t('header.register')}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        <ProfileDrawer open={openDrawer} onClose={() => setOpenDrawer(false)} />
      </header>
      <div className="lg:hidden relative w-full bg-section-gradient mt-[70px]">
        <div className="h-[55px] overflow-x-auto overflow-y-hidden [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <div className="flex items-center gap-1 h-full w-max px-2">
            {mobileHeaderItems.map((item) => {
              const countKey = sportCountKey[item.title];
              const liveCount = countKey ? (inplaySportCounts[countKey] ?? 0) : undefined;

              return (
                <div
                  key={item.id}
                  className="text-headerText border-b-[3px] border-transparent group shrink-0"
                >
                  <Link
                    to={item.path}
                    className="flex flex-col items-center text-black text-[11px] font-bold uppercase px-2 py-[5px] relative"
                  >
                    {liveCount !== undefined && (
                      <div className="absolute top-2 right-0 flex items-center shadow-xl whitespace-nowrap    text-[8px] font-bold leading-none ">
                        <div className=" bg-[#FBFBFB] text-[#EF1717] rounded-l-[3px] text-[7px] px-1 py-0.5 pt-[3px] h-3">
                          <span className="animate-pulse">LIVE</span>
                        </div>
                        <div className="bg-[#EF1717] text-white rounded-r-[3px] text-[8px] leading-none px-0.5 py-[1px] pt-[2px] h-3">
                          {liveCount}
                        </div>
                      </div>
                    )}
                    <span
                      className={`flex size-[27px] overflow-hidden items-center justify-center rounded-[50px] group-hover:bg-white ${pathname === `/${item.title}` ? 'bg-white' : ''}`}
                      // bg-[#1f2c38]
                    >
                      <img className="w-[30px] h-auto" src={item.imgUrl} alt={item.title} />
                    </span>
                    {t(`header.sports.${sportTitleI18nKey[item.title] ?? item.title}`, {
                      defaultValue: item.title,
                    })}
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
